import { nodePathToFriendlyName } from '@ourbranch/lookups';
import { toCapitalized } from 'core/helpers/formatters';

/**
 * Processes driver and non-driver related changes from diff data
 * @param {Array} filteredFieldPaths - All field paths from changes
 * @param {Object} addedChanges - Added changes from diff
 * @param {Object} removedChanges - Removed changes from diff
 * @param {Array} existingDrivers - Existing drivers from lastSegment
 * @param {Array} existingNonDrivers - Existing non-drivers from lastSegment
 * @returns {Object} Object containing driver changes and metadata
 */
function processDriverChanges(
  filteredFieldPaths,
  addedChanges,
  removedChanges,
  existingDrivers = [],
  existingNonDrivers = []
) {
  const changesList = [];

  // Find all driver and non-driver indexes and classify them
  const driverIndexes = new Set();
  const nonDriverIndexes = new Set();

  filteredFieldPaths.forEach((path) => {
    const driverMatch = path.match(/^drivers\.(\d+)\./);
    const nonDriverMatch = path.match(/^nonDrivers\.(\d+)\./);

    if (driverMatch) {
      driverIndexes.add(driverMatch[1]);
    }
    if (nonDriverMatch) {
      nonDriverIndexes.add(nonDriverMatch[1]);
    }
  });

  // Helper function to detect shifted drivers (Rule 4)
  const detectShiftedDrivers = () => {
    const shiftedDriverIds = new Set();
    const shiftedNonDriverIds = new Set();

    // For each driver index in addedChanges, check if it matches a driver in removedChanges
    driverIndexes.forEach((addedIndex) => {
      const addedId = addedChanges[`drivers.${addedIndex}.id`];
      const addedFirstName = addedChanges[`drivers.${addedIndex}.firstName`];
      const addedLastName = addedChanges[`drivers.${addedIndex}.lastName`];

      if (addedId && addedFirstName) {
        // Check if this driver exists in removedChanges at a different index
        driverIndexes.forEach((removedIndex) => {
          if (addedIndex !== removedIndex) {
            const removedId = removedChanges[`drivers.${removedIndex}.id`];
            const removedFirstName = removedChanges[`drivers.${removedIndex}.firstName`];
            const removedLastName = removedChanges[`drivers.${removedIndex}.lastName`];

            // If ID matches OR (firstName and lastName match), this is a shift
            if (addedId === removedId || (addedFirstName === removedFirstName && addedLastName === removedLastName)) {
              shiftedDriverIds.add(addedId);
            }
          }
        });
      }
    });

    // Same logic for non-drivers
    nonDriverIndexes.forEach((addedIndex) => {
      const addedId = addedChanges[`nonDrivers.${addedIndex}.id`];
      const addedFirstName = addedChanges[`nonDrivers.${addedIndex}.firstName`];
      const addedLastName = addedChanges[`nonDrivers.${addedIndex}.lastName`];

      if (addedId && addedFirstName) {
        nonDriverIndexes.forEach((removedIndex) => {
          if (addedIndex !== removedIndex) {
            const removedId = removedChanges[`nonDrivers.${removedIndex}.id`];
            const removedFirstName = removedChanges[`nonDrivers.${removedIndex}.firstName`];
            const removedLastName = removedChanges[`nonDrivers.${removedIndex}.lastName`];

            if (addedId === removedId || (addedFirstName === removedFirstName && addedLastName === removedLastName)) {
              shiftedNonDriverIds.add(addedId);
            }
          }
        });
      }
    });

    return { shiftedDriverIds, shiftedNonDriverIds };
  };

  const { shiftedDriverIds, shiftedNonDriverIds } = detectShiftedDrivers();

  // Helper function to get driver/non-driver name with fallback to existing data
  const getDriverName = (index, pathPrefix, isDriver = true) => {
    const firstNamePath = `${pathPrefix}.${index}.firstName`;
    const lastNamePath = `${pathPrefix}.${index}.lastName`;

    // Try to get name from changes first
    let firstName = addedChanges[firstNamePath] || removedChanges[firstNamePath];
    let lastName = addedChanges[lastNamePath] || removedChanges[lastNamePath];

    // If not found in changes, try to get from existing data
    if (!firstName) {
      const existingArray = isDriver ? existingDrivers : existingNonDrivers;
      const existingItem = existingArray?.[parseInt(index, 10)];
      if (existingItem) {
        firstName = existingItem.firstName;
        lastName = existingItem.lastName;
      }
    }

    if (firstName) {
      return `${firstName}${lastName ? ` ${lastName}` : ''}`;
    }

    return null;
  };

  // Helper function to process individual field changes for a driver/non-driver
  const processIndividualFieldChanges = (indexType, index, pathPrefix) => {
    const relevantPaths = filteredFieldPaths.filter(
      (path) => path.startsWith(`${pathPrefix}.${index}.`) && !path.endsWith('.lastName')
    );

    relevantPaths.forEach((fieldPath) => {
      let friendlyName = nodePathToFriendlyName[fieldPath] ?? toCapitalized(fieldPath);
      const rawOldValue = removedChanges[fieldPath];
      const rawNewValue = addedChanges[fieldPath];

      // Replace driver/non-driver indexes with actual names in field names
      if (fieldPath.match(/^drivers\.(\d+)\./)) {
        const driverIndex = fieldPath.match(/^drivers\.(\d+)/)?.[1];
        const fullName = getDriverName(driverIndex, 'drivers', true);

        if (fullName) {
          friendlyName = friendlyName.replace(/Drivers \d+/g, `Driver ${fullName}`);
        }
      }

      if (fieldPath.match(/^nonDrivers\.(\d+)\./)) {
        const nonDriverIndex = fieldPath.match(/^nonDrivers\.(\d+)/)?.[1];
        const fullName = getDriverName(nonDriverIndex, 'nonDrivers', false);

        if (fullName) {
          friendlyName = friendlyName.replace(/Non Drivers \d+/g, `Non-Driver ${fullName}`);
        }
      }

      // Convert values to strings for proper display, especially for boolean values and objects
      const formatValue = (value) => {
        if (typeof value === 'boolean') {
          return value.toString();
        }
        if (value === null) {
          return 'null';
        }
        if (typeof value === 'object') {
          return JSON.stringify(value);
        }
        return value;
      };

      const oldValue = formatValue(rawOldValue);
      const newValue = formatValue(rawNewValue);

      if (rawOldValue !== undefined && rawNewValue !== undefined) {
        // Field was changed from one value to another
        changesList.push({
          type: 'change',
          friendlyName,
          oldValue,
          newValue
        });
      } else if (rawNewValue !== undefined) {
        // Field was added
        changesList.push({
          type: 'added',
          friendlyName,
          addedValue: newValue
        });
      } else if (rawOldValue !== undefined) {
        // Field was removed
        changesList.push({
          type: 'removed',
          friendlyName,
          removedValue: oldValue
        });
      }
    });
  };

  // Helper function to process all fields for added/removed drivers
  const processAllFieldsForDriver = (index, pathPrefix, changeType, dataSource, isDriver = true) => {
    const fullName = getDriverName(index, pathPrefix, isDriver);
    if (!fullName) {
      return;
    }

    // First add the driver/non-driver summary entry
    const entityType = isDriver ? 'Driver' : 'Non-Driver';
    changesList.push({
      type: changeType,
      friendlyName: `${entityType} ${fullName}`,
      [changeType === 'added' ? 'addedValue' : 'removedValue']: changeType
    });

    // Then add all individual field entries
    const relevantPaths = filteredFieldPaths.filter(
      (path) => path.startsWith(`${pathPrefix}.${index}.`) && !path.endsWith('.lastName') && !path.endsWith('.id')
    );

    relevantPaths.forEach((fieldPath) => {
      const fieldValue = dataSource[fieldPath];
      if (fieldValue !== undefined) {
        const fieldName = fieldPath.replace(`${pathPrefix}.${index}.`, '');
        const friendlyFieldName = `${entityType} ${fullName} ${toCapitalized(fieldName)}`;

        // Convert values to strings for proper display
        const formatValue = (value) => {
          if (typeof value === 'boolean') {
            return value.toString();
          }
          if (value === null) {
            return 'null';
          }
          if (typeof value === 'object') {
            return JSON.stringify(value);
          }
          return value;
        };

        changesList.push({
          type: changeType,
          friendlyName: friendlyFieldName,
          [changeType === 'added' ? 'addedValue' : 'removedValue']: formatValue(fieldValue)
        });
      }
    });
  };

  // Process each driver index
  driverIndexes.forEach((driverIndex) => {
    const driverId = addedChanges[`drivers.${driverIndex}.id`] || removedChanges[`drivers.${driverIndex}.id`];

    // Rule 4: Skip shifted drivers by checking if this specific driver ID is shifted
    if (driverId && shiftedDriverIds.has(driverId)) {
      return;
    }

    const hasIdInAdded = addedChanges[`drivers.${driverIndex}.id`];
    const hasIdInRemoved = removedChanges[`drivers.${driverIndex}.id`];

    if (hasIdInAdded) {
      // Rule 1: New added driver - show driver summary and all fields as Added
      processAllFieldsForDriver(driverIndex, 'drivers', 'added', addedChanges, true);
    } else if (hasIdInRemoved) {
      // Rule 2: Removed driver - show driver summary and all fields as Removed
      processAllFieldsForDriver(driverIndex, 'drivers', 'removed', removedChanges, true);
    } else {
      // Rule 3: Same driver with changes - show individual field changes
      processIndividualFieldChanges('driver', driverIndex, 'drivers');
    }
  });

  // Process each non-driver index (same logic)
  nonDriverIndexes.forEach((nonDriverIndex) => {
    const nonDriverId =
      addedChanges[`nonDrivers.${nonDriverIndex}.id`] || removedChanges[`nonDrivers.${nonDriverIndex}.id`];

    // Rule 4: Skip shifted non-drivers by checking if this specific non-driver ID is shifted
    if (nonDriverId && shiftedNonDriverIds.has(nonDriverId)) {
      return;
    }

    const hasIdInAdded = addedChanges[`nonDrivers.${nonDriverIndex}.id`];
    const hasIdInRemoved = removedChanges[`nonDrivers.${nonDriverIndex}.id`];

    if (hasIdInAdded) {
      // Rule 1: New added non-driver - show non-driver summary and all fields as Added
      processAllFieldsForDriver(nonDriverIndex, 'nonDrivers', 'added', addedChanges, false);
    } else if (hasIdInRemoved) {
      // Rule 2: Removed non-driver - show non-driver summary and all fields as Removed
      processAllFieldsForDriver(nonDriverIndex, 'nonDrivers', 'removed', removedChanges, false);
    } else {
      // Rule 3: Same non-driver with changes - show individual field changes
      processIndividualFieldChanges('nonDriver', nonDriverIndex, 'nonDrivers');
    }
  });

  // Process removedDrivers entries (these are always removed drivers)
  filteredFieldPaths.forEach((path) => {
    if (path.match(/^removedDrivers\.(\d+)\.firstName$/) && addedChanges[path]) {
      const removedDriverIndex = path.match(/^removedDrivers\.(\d+)/)?.[1];
      const firstName = addedChanges[path];
      const lastName = addedChanges[`removedDrivers.${removedDriverIndex}.lastName`];

      if (firstName) {
        const fullName = `${firstName}${lastName ? ` ${lastName}` : ''}`;
        changesList.push({
          type: 'removed',
          friendlyName: `Driver ${fullName}`,
          removedValue: 'removed'
        });

        // Add all individual fields for this removed driver
        const removedDriverPaths = filteredFieldPaths.filter(
          (fieldPath) =>
            fieldPath.startsWith(`removedDrivers.${removedDriverIndex}.`) &&
            !fieldPath.endsWith('.lastName') &&
            !fieldPath.endsWith('.id')
        );

        removedDriverPaths.forEach((fieldPath) => {
          const fieldValue = addedChanges[fieldPath];
          if (fieldValue !== undefined) {
            const fieldName = fieldPath.replace(`removedDrivers.${removedDriverIndex}.`, '');
            const friendlyFieldName = `Driver ${fullName} ${toCapitalized(fieldName)}`;

            const formatValue = (value) => {
              if (typeof value === 'boolean') {
                return value.toString();
              }
              if (value === null) {
                return 'null';
              }
              if (typeof value === 'object') {
                return JSON.stringify(value);
              }
              return value;
            };

            changesList.push({
              type: 'removed',
              friendlyName: friendlyFieldName,
              removedValue: formatValue(fieldValue)
            });
          }
        });
      }
    }
  });

  // Process removedNonDrivers entries (these are always removed non-drivers)
  filteredFieldPaths.forEach((path) => {
    if (path.match(/^removedNonDrivers\.(\d+)\.firstName$/) && addedChanges[path]) {
      const removedNonDriverIndex = path.match(/^removedNonDrivers\.(\d+)/)?.[1];
      const firstName = addedChanges[path];
      const lastName = addedChanges[`removedNonDrivers.${removedNonDriverIndex}.lastName`];

      if (firstName) {
        const fullName = `${firstName}${lastName ? ` ${lastName}` : ''}`;
        changesList.push({
          type: 'removed',
          friendlyName: `Non-Driver ${fullName}`,
          removedValue: 'removed'
        });

        // Add all individual fields for this removed non-driver
        const removedNonDriverPaths = filteredFieldPaths.filter(
          (fieldPath) =>
            fieldPath.startsWith(`removedNonDrivers.${removedNonDriverIndex}.`) &&
            !fieldPath.endsWith('.lastName') &&
            !fieldPath.endsWith('.id')
        );

        removedNonDriverPaths.forEach((fieldPath) => {
          const fieldValue = addedChanges[fieldPath];
          if (fieldValue !== undefined) {
            const fieldName = fieldPath.replace(`removedNonDrivers.${removedNonDriverIndex}.`, '');
            const friendlyFieldName = `Non-Driver ${fullName} ${toCapitalized(fieldName)}`;

            const formatValue = (value) => {
              if (typeof value === 'boolean') {
                return value.toString();
              }
              if (value === null) {
                return 'null';
              }
              if (typeof value === 'object') {
                return JSON.stringify(value);
              }
              return value;
            };

            changesList.push({
              type: 'removed',
              friendlyName: friendlyFieldName,
              removedValue: formatValue(fieldValue)
            });
          }
        });
      }
    }
  });

  // Helper function to check if a field path should be skipped for driver processing
  const shouldSkipDriverField = (fieldPath) => {
    // Skip all driver and non-driver fields as they're now handled in this function
    if (fieldPath.startsWith('drivers.') || fieldPath.startsWith('nonDrivers.')) {
      return true;
    }

    // Skip all removedDrivers and removedNonDrivers fields as they're handled above
    if (fieldPath.startsWith('removedDrivers.') || fieldPath.startsWith('removedNonDrivers.')) {
      return true;
    }

    return false;
  };

  return {
    driverChanges: changesList,
    shouldSkipDriverField
  };
}

/**
 * Processes car related changes from diff data
 * @param {Array} filteredFieldPaths - All field paths from changes
 * @param {Object} addedChanges - Added changes from diff
 * @param {Object} removedChanges - Removed changes from diff
 * @param {Array} existingCars - Existing cars from lastSegment
 * @returns {Object} Object containing car changes and metadata
 */
function processCarChanges(filteredFieldPaths, addedChanges, removedChanges, existingCars = []) {
  const changesList = [];

  // Find all car indexes
  const carIndexes = new Set();

  filteredFieldPaths.forEach((path) => {
    const carMatch = path.match(/^cars\.(\d+)\./);

    if (carMatch) {
      carIndexes.add(carMatch[1]);
    }
  });

  // Helper function to detect shifted cars (Rule 4)
  const detectShiftedCars = () => {
    const shiftedVins = new Set();

    // For each car index in addedChanges, check if it matches a car in removedChanges
    carIndexes.forEach((addedIndex) => {
      const addedVin = addedChanges[`cars.${addedIndex}.VIN`];

      if (addedVin) {
        // Check if this car exists in removedChanges at a different index
        carIndexes.forEach((removedIndex) => {
          if (addedIndex !== removedIndex) {
            const removedVin = removedChanges[`cars.${removedIndex}.VIN`];

            // If VIN matches, this specific VIN is shifted
            if (addedVin === removedVin) {
              shiftedVins.add(addedVin);
            }
          }
        });
      }
    });

    return { shiftedVins };
  };

  const { shiftedVins } = detectShiftedCars();

  // Helper function to get car name with fallback to existing data
  const getCarName = (index) => {
    const makePath = `cars.${index}.make`;
    const modelPath = `cars.${index}.model`;
    const vinPath = `cars.${index}.VIN`;

    // Try to get make and model from changes first
    let make = addedChanges[makePath] || removedChanges[makePath];
    let model = addedChanges[modelPath] || removedChanges[modelPath];
    let vin = addedChanges[vinPath] || removedChanges[vinPath];

    // If not found in changes, try to get from existing data
    if (!make || !model) {
      const existingCar = existingCars?.[parseInt(index, 10)];
      if (existingCar) {
        make = make || existingCar.make;
        model = model || existingCar.model;
        vin = vin || existingCar.VIN;
      }
    }

    // Prefer make + model combination
    if (make && model) {
      return `${make} ${model}`;
    }
    if (make) {
      return make;
    }
    if (vin) {
      return `Vehicle ${vin.slice(-4)}`;
    }

    return `Car ${index}`;
  };

  // Helper function to process individual field changes for a car
  const processIndividualFieldChanges = (index) => {
    const relevantPaths = filteredFieldPaths.filter((path) => path.startsWith(`cars.${index}.`));

    relevantPaths.forEach((fieldPath) => {
      let friendlyName = nodePathToFriendlyName[fieldPath] ?? toCapitalized(fieldPath);
      const rawOldValue = removedChanges[fieldPath];
      const rawNewValue = addedChanges[fieldPath];

      // Replace car indexes with actual names in field names
      const carName = getCarName(index);
      friendlyName = friendlyName.replace(/Cars \d+/g, `Car ${carName}`);

      // Convert values to strings for proper display, especially for boolean values and objects
      const formatValue = (value) => {
        if (typeof value === 'boolean') {
          return value.toString();
        }
        if (value === null) {
          return 'null';
        }
        if (typeof value === 'object') {
          return JSON.stringify(value);
        }
        return value;
      };

      const oldValue = formatValue(rawOldValue);
      const newValue = formatValue(rawNewValue);

      if (rawOldValue !== undefined && rawNewValue !== undefined) {
        // Field was changed from one value to another
        changesList.push({
          type: 'change',
          friendlyName,
          oldValue,
          newValue
        });
      } else if (rawNewValue !== undefined) {
        // Field was added
        changesList.push({
          type: 'added',
          friendlyName,
          addedValue: newValue
        });
      } else if (rawOldValue !== undefined) {
        // Field was removed
        changesList.push({
          type: 'removed',
          friendlyName,
          removedValue: oldValue
        });
      }
    });
  };

  // Helper function to process all fields for added/removed cars
  const processAllFieldsForCar = (index, changeType, dataSource) => {
    const carName = getCarName(index);

    // First add the car summary entry
    changesList.push({
      type: changeType,
      friendlyName: `Car ${carName}`,
      [changeType === 'added' ? 'addedValue' : 'removedValue']: changeType
    });

    // Then add all individual field entries
    const relevantPaths = filteredFieldPaths.filter((path) => path.startsWith(`cars.${index}.`));

    relevantPaths.forEach((fieldPath) => {
      const fieldValue = dataSource[fieldPath];
      if (fieldValue !== undefined) {
        const fieldName = fieldPath.replace(`cars.${index}.`, '');
        const friendlyFieldName = `Car ${carName} ${toCapitalized(fieldName)}`;

        // Convert values to strings for proper display
        const formatValue = (value) => {
          if (typeof value === 'boolean') {
            return value.toString();
          }
          if (value === null) {
            return 'null';
          }
          if (typeof value === 'object') {
            return JSON.stringify(value);
          }
          return value;
        };

        changesList.push({
          type: changeType,
          friendlyName: friendlyFieldName,
          [changeType === 'added' ? 'addedValue' : 'removedValue']: formatValue(fieldValue)
        });
      }
    });
  };

  // Process each car index
  carIndexes.forEach((carIndex) => {
    const hasVinInAdded = addedChanges[`cars.${carIndex}.VIN`];
    const hasVinInRemoved = removedChanges[`cars.${carIndex}.VIN`];

    // Rule 4: Skip if this specific car (by VIN) is shifted
    const isAddedCarShifted = hasVinInAdded && shiftedVins.has(hasVinInAdded);
    const isRemovedCarShifted = hasVinInRemoved && shiftedVins.has(hasVinInRemoved);

    if (hasVinInAdded && !isAddedCarShifted) {
      // Rule 1: New added car (not a shift) - show car summary and all fields as Added
      processAllFieldsForCar(carIndex, 'added', addedChanges);
    } else if (hasVinInRemoved && !isRemovedCarShifted) {
      // Rule 2: Removed car (not a shift) - show car summary and all fields as Removed
      processAllFieldsForCar(carIndex, 'removed', removedChanges);
    } else if (!hasVinInAdded && !hasVinInRemoved) {
      // Rule 3: Same car with changes - show individual field changes
      processIndividualFieldChanges(carIndex);
    }
    // Rule 4: If either car is shifted, we skip showing anything for this index
  });

  // Helper function to check if a field path should be skipped for car processing
  const shouldSkipCarField = (fieldPath) => {
    // Skip all car fields as they're now handled in this function
    if (fieldPath.startsWith('cars.')) {
      return true;
    }

    return false;
  };

  return {
    carChanges: changesList,
    shouldSkipCarField
  };
}

export function parseRequestedChanges(diff, lastSegment = {}) {
  const { drivers: existingDrivers, nonDrivers: existingNonDrivers, cars: existingCars } = lastSegment;

  let diffObj;
  try {
    diffObj = diff && JSON.parse(diff);
  } catch (error) {
    console.error('Failed to parse diff:', error);
    return [];
  }

  const addedChanges = diffObj?.filtered?.addedChanges || {};
  const removedChanges = diffObj?.filtered?.removedChanges || {};

  const changesList = [];

  // Array of keys to ignore in the changes display
  const ignoredKeys = ['hash', 'id', 'removedTimestamp', 'image'];

  // Get all unique field paths from both added and removed changes
  const allFieldPaths = new Set([...Object.keys(addedChanges), ...Object.keys(removedChanges)]);

  // Filter out ignored keys - only ignore if the field path ends with the ignored key or contains it as a standalone segment
  const filteredFieldPaths = Array.from(allFieldPaths).filter((fieldPath) => {
    return !ignoredKeys.some((ignoredKey) => {
      // Check if the field path ends with the ignored key
      if (fieldPath.endsWith(`.${ignoredKey}`) || fieldPath === ignoredKey) {
        return true;
      }
      // Check if the ignored key appears as a standalone segment in the path
      const pathSegments = fieldPath.split('.');
      return pathSegments.includes(ignoredKey);
    });
  });

  // Process all driver-related changes
  const { driverChanges, shouldSkipDriverField } = processDriverChanges(
    filteredFieldPaths,
    addedChanges,
    removedChanges,
    existingDrivers,
    existingNonDrivers
  );

  // Add driver changes to the main changes list
  changesList.push(...driverChanges);

  // Process all car-related changes
  const { carChanges, shouldSkipCarField } = processCarChanges(
    filteredFieldPaths,
    addedChanges,
    removedChanges,
    existingCars
  );

  // Add car changes to the main changes list
  changesList.push(...carChanges);

  // Sort field paths: drivers first (in order), then non-drivers, then cars, then the rest
  const sortedFieldPaths = filteredFieldPaths.sort((a, b) => {
    const getPathPriority = (path) => {
      if (path.startsWith('drivers.')) {
        const match = path.match(/^drivers\.(\d+)/);
        const driverIndex = match ? parseInt(match[1], 10) : 0;
        return { priority: 1, subPriority: driverIndex };
      }
      if (path.startsWith('nonDrivers.')) {
        return { priority: 2, subPriority: 0 };
      }
      if (path.startsWith('cars.') || path.startsWith('vehicles.')) {
        const match = path.match(/^(?:cars|vehicles)\.(\d+)/);
        const carIndex = match ? parseInt(match[1], 10) : 0;
        return { priority: 3, subPriority: carIndex };
      }
      return { priority: 4, subPriority: 0 };
    };

    const priorityA = getPathPriority(a);
    const priorityB = getPathPriority(b);

    if (priorityA.priority !== priorityB.priority) {
      return priorityA.priority - priorityB.priority;
    }
    if (priorityA.subPriority !== priorityB.subPriority) {
      return priorityA.subPriority - priorityB.subPriority;
    }
    return a.localeCompare(b); // Alphabetical for same priority and subPriority
  });

  for (const fieldPath of sortedFieldPaths) {
    // Skip driver fields that are handled by the driver processing function
    if (shouldSkipDriverField(fieldPath)) {
      continue;
    }

    // Skip car fields that are handled by the car processing function
    if (shouldSkipCarField(fieldPath)) {
      continue;
    }

    const friendlyName = nodePathToFriendlyName[fieldPath] ?? toCapitalized(fieldPath);
    const rawOldValue = removedChanges[fieldPath];
    const rawNewValue = addedChanges[fieldPath];

    // Convert values to strings for proper display, especially for boolean values and objects
    const formatValue = (value) => {
      if (typeof value === 'boolean') {
        return value.toString();
      }
      if (value === null) {
        return 'null';
      }
      if (typeof value === 'object') {
        return JSON.stringify(value);
      }
      return value;
    };

    const oldValue = formatValue(rawOldValue);
    const newValue = formatValue(rawNewValue);

    if (rawOldValue !== undefined && rawNewValue !== undefined) {
      // Field was changed from one value to another
      changesList.push({
        type: 'change',
        friendlyName,
        oldValue,
        newValue
      });
    } else if (rawNewValue !== undefined) {
      // Field was added
      changesList.push({
        type: 'added',
        friendlyName,
        addedValue: newValue
      });
    } else if (rawOldValue !== undefined) {
      // Field was removed
      changesList.push({
        type: 'removed',
        friendlyName,
        removedValue: oldValue
      });
    }
  }

  return changesList;
}
